<template>
    <section
        class="tw:py-6 lg:tw:py-10 tw:mb-3 tw:mb-lg-4 tw:lightgray-content tw:px-3 lg:tw:px-6"
        data-test="identificacion-privada-step"
    >
        <div class="tw:max-w-lg lg:tw:max-w-2xl tw:mx-auto">
            <CardFront
                top-bar
                data-test="identificacion-card"
            >
                <div class="tw:space-y-5 lg:tw:space-y-6">
                    <header
                        class="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:mb-2"
                        data-test="identificacion-header"
                    >
                        <span
                            class="tw:inline-flex tw:items-center tw:justify-center tw:w-16 tw:h-16 tw:rounded-full tw:bg-primary-50 tw:text-primary-500"
                            aria-hidden="true"
                            data-test="identificacion-icon"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />
                                <circle cx="12" cy="10" r="2.5" />
                                <path d="M9.5 14.5c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5" />
                            </svg>
                        </span>
                        <h2
                            class="tw:h1 tw:text-uppercase tw:text-center tw:m-0"
                            data-test="identificacion-title"
                        >
                            {{ $t('nuevo_abono.identificacion_titulo') }}
                        </h2>
                    </header>
                    <p
                        class="tw:subtitle tw:h3 tw:light tw:mb-4 tw:text-center"
                        data-test="identificacion-descripcion"
                    >
                        {{ $t('nuevo_abono.identificacion_descripcion') }}
                    </p>

                    <div
                        v-if="referente"
                        class="tw:bg-emerald-50 tw:border tw:border-emerald-200 tw:text-emerald-800 tw:px-4 tw:py-3 tw:rounded-lg"
                        role="status"
                        data-test="identificacion-correcta-banner"
                    >
                        {{ $t('nuevo_abono.identificacion_correcta', {
                            name: referente.name,
                            surnames: referente.surnames,
                            n_abonado: referente.n_abonado
                        }) }}
                    </div>

                    <div
                        v-if="error === 'no_encontrado'"
                        class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg"
                        role="alert"
                        data-test="error-no-encontrado"
                    >
                        {{ $t('nuevo_abono.identificacion_error_no_encontrado') }}
                    </div>
                    <div
                        v-else-if="error === 'validacion'"
                        class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg"
                        role="alert"
                        data-test="error-validacion"
                    >
                        {{ $t('nuevo_abono.identificacion_error_validacion') }}
                    </div>
                    <div
                        v-else-if="error"
                        class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg"
                        role="alert"
                        data-test="error-inesperado"
                    >
                        {{ $t('nuevo_abono.identificacion_error_inesperado') }}
                    </div>

                    <form
                        @submit.prevent="onSubmit"
                        novalidate
                        data-test="identificacion-form"
                    >
                        <InputText
                            :label="$t('nuevo_abono.identificacion_n_abonado_label')"
                            name="n_abonado"
                            :value="n_abonado_input"
                            :rules="'required'"
                            :disabled="loading"
                            :maxlength="20"
                            :api-validation-error="errorsValidacion.n_abonado"
                            icon='<path stroke-linecap="round" stroke-linejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.294.876 6.75 6.75 0 0 1-3.294-.876V9l3.294 3.336Z" />'
                            data-test="identificacion-n-abonado-input"
                            @input="val => { n_abonado_input = val; }"
                        />
                        <div
                            class="tw:text-xs tw:text-gray-500 tw:mt-1"
                            data-test="identificacion-n-abonado-helper"
                        >
                            {{ $t('nuevo_abono.identificacion_n_abonado_helper') }}
                        </div>

                        <InputText
                            :label="$t('nuevo_abono.identificacion_dni_label')"
                            name="dni"
                            :value="dni_input"
                            :rules="'required'"
                            :disabled="loading"
                            :maxlength="20"
                            :api-validation-error="errorsValidacion.dni"
                            icon='<path stroke-linecap="round" stroke-linejoin="round" d="M10.5 21h5.25a2.25 2.25 0 0 0 2.25-2.25v-4.5a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 14.25v4.5A2.25 2.25 0 0 0 4.5 16.5h3.75M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />'
                            data-test="identificacion-dni-input"
                            @input="val => { dni_input = val; }"
                        />
                        <div
                            class="tw:text-xs tw:text-gray-500 tw:mt-1"
                            data-test="identificacion-dni-helper"
                        >
                            {{ $t('nuevo_abono.identificacion_dni_helper') }}
                        </div>

                        <Button
                            type="submit"
                            :loading="loading"
                            :disabled="loading"
                            :aria-busy="loading ? 'true' : 'false'"
                            variant="dark"
                            custom-class="tw:mt-6 tw:w-full"
                            data-test="identificacion-submit-button"
                        >
                            <span v-if="loading">{{ $t('nuevo_abono.identificacion_submit_loading') }}</span>
                            <span v-else>{{ $t('nuevo_abono.identificacion_submit') }}</span>
                        </Button>

                        <Button
                            v-if="referente || error"
                            variant="default-soft"
                            custom-class="tw:mt-3 tw:w-full"
                            data-test="reset-button"
                            @click="onReset"
                        >
                            {{ $t('nuevo_abono.identificacion_reintentar_label') }}
                        </Button>
                    </form>
                </div>
            </CardFront>
        </div>
    </section>
</template>

<script setup>
    import { ref } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import Button from '@/modules/front/ui/Button.vue';

    const props = defineProps({
        loading: { type: Boolean, required: true },
        error: { type: String, default: null },
        errorsValidacion: { type: Object, default: () => ({}) },
        referente: { type: Object, default: null },
    });

    const emit = defineEmits(['submit', 'reset']);

    const n_abonado_input = ref('');
    const dni_input = ref('');

    function onSubmit() {
        if (props.loading) return;
        emit('submit', {
            n_abonado: n_abonado_input.value,
            dni: dni_input.value
        });
    }

    function onReset() {
        n_abonado_input.value = '';
        dni_input.value = '';
        emit('reset');
    }
</script>
